import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { toast } from 'sonner';
import { ShopifyProduct, storefrontApiRequest, formatPrice } from '@/lib/shopify';
import { trackAddToCart } from '@/lib/analytics';
import { getEditorialSource, saveEditorialOutcome } from '@/lib/editorialAttribution';
import { saveEmailOutcome } from '@/lib/emailAttribution';

export interface CartItem {
  lineId: string | null;
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  quantity: number;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
}

/** Why a line differs from what the shopper asked for, and when that was learned. */
export interface LineNotice {
  message: string;
  at: number;
}

/** What Shopify did with an add, which can be less than was asked. */
export interface CartChange {
  /** The cart holds this item. False when it is sold out or the request failed. */
  ok: boolean;
  /** Units this add really put in the cart. */
  addedQuantity: number;
  /** Set when the cart holds fewer than asked. The store has already shown it as a toast. */
  notice?: string;
}

interface CartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  /** Shopify's subtotal for the lines shown; null until a cart has been read back. */
  subtotal: number | null;
  /** By variantId: why a line differs from what the shopper asked for (capped quantity, new price). */
  notices: Record<string, LineNotice>;
  /** Lines that left the cart without the shopper removing them here, and why. */
  removedNotices: Array<{ variantId: string; message: string }>;
  isLoading: boolean;
  isSyncing: boolean;

  addItem: (item: Omit<CartItem, 'lineId'>) => Promise<CartChange>;
  /** Both resolve true once the change is in the cart, false when it is not. */
  updateQuantity: (variantId: string, quantity: number) => Promise<boolean>;
  removeItem: (variantId: string) => Promise<boolean>;
  clearCart: () => void;
  dismissRemovedNotices: () => void;
  syncCart: () => Promise<void>;
  getCheckoutUrl: () => string | null;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  /** Attach the shopper's gift note to the Shopify cart so the fulfiller sees it. */
  setGiftNote: (message: string) => Promise<void>;
}

// ─────────────────────────────────────────────────────────────
// Attribution attributes
//
// We have no Admin API access to the store that fulfils the order, so the only
// way a sale can ever be traced back to a LoftLoom click is via cart attributes,
// which persist onto the resulting Shopify order.
const REF_KEY = 'loftloom-ref';

function getOrCreateRef(): string {
  try {
    const existing = localStorage.getItem(REF_KEY);
    if (existing) return existing;
    const ref =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(REF_KEY, ref);
    return ref;
  } catch {
    return 'unavailable';
  }
}

function attributionAttributes(): Array<{ key: string; value: string }> {
  const attrs: Array<{ key: string; value: string }> = [];
  try {
    attrs.push({ key: 'loftloom_ref', value: getOrCreateRef() });

    const src = window.location.pathname + window.location.search || document.referrer || 'direct';
    attrs.push({ key: 'loftloom_src', value: src.slice(0, 250) });

    const gclid = new URLSearchParams(window.location.search).get('gclid');
    if (gclid) attrs.push({ key: 'loftloom_gclid', value: gclid.slice(0, 250) });
  } catch {
    /* attribution is best-effort and must never block a cart */
  }
  return attrs;
}

// Every cart call waits its turn (see inTurn below), so one that never got an
// answer held ADD TO CART and every other cart control until the page was
// reloaded. A call is given up on after this long; its caller's catch tells
// the shopper, and the calls queued behind it run.
const CART_TIMEOUT_MS = 8000;

async function cartRequest(query: string, variables: Record<string, unknown>) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CART_TIMEOUT_MS);
  try {
    return await storefrontApiRequest(query, variables, controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

// Cart GraphQL mutations
//
// Every call selects the same lines, so the cart on screen is always rebuilt
// from what Shopify holds (see reconcile below), never from what was asked for.
// A write also selects `warnings`: the only place Shopify says that a quantity
// came back lower because of stock.
const CART_FIELDS = `
  id
  totalQuantity
  cost { subtotalAmount { amount currencyCode } }
  lines(first: 100) {
    edges {
      node {
        id
        quantity
        cost { amountPerQuantity { amount currencyCode } }
        merchandise {
          ... on ProductVariant {
            id
            title
            availableForSale
            selectedOptions { name value }
            product { id handle title productType featuredImage { url altText } }
          }
        }
      }
    }
  }
`;

const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) { ${CART_FIELDS} }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart { checkoutUrl ${CART_FIELDS} }
      userErrors { field message }
      warnings { code target }
    }
  }
`;

const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
      warnings { code target }
    }
  }
`;

const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
      warnings { code target }
    }
  }
`;

const CART_ATTRIBUTES_UPDATE_MUTATION = `
  mutation cartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart { id }
      userErrors { field message }
    }
  }
`;

const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
      warnings { code target }
    }
  }
`;

type Money = { amount: string; currencyCode: string };

interface ShopifyCartLine {
  id: string;
  quantity: number;
  cost: { amountPerQuantity: Money };
  merchandise: {
    id: string;
    title: string;
    availableForSale: boolean;
    selectedOptions: Array<{ name: string; value: string }>;
    product: {
      id: string;
      handle: string;
      title: string;
      productType: string;
      featuredImage: { url: string; altText: string | null } | null;
    };
  };
}

interface ShopifyCart {
  id: string;
  checkoutUrl?: string;
  totalQuantity: number;
  cost: { subtotalAmount: Money };
  lines: { edges: Array<{ node: ShopifyCartLine }> };
}

type UserError = { field: string[] | null; message: string };
/** `target` is the id of the cart line the warning is about. */
type CartWarning = { code: string; target: string };
type CartAnswer = { cart?: ShopifyCart; warnings?: CartWarning[]; cartNotFound?: boolean };

function formatCheckoutUrl(checkoutUrl: string): string {
  try {
    const url = new URL(checkoutUrl);
    url.searchParams.set('channel', 'online_store');
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

function isCartNotFoundError(userErrors: UserError[]): boolean {
  // Only an error about the cart itself ("The specified cart does not exist.").
  // "The merchandise line with id … does not exist" (a line removed in another
  // tab) and "The merchandise with id … does not exist" (a delisted product)
  // also say "does not exist", and used to empty the shopper's whole cart.
  return userErrors.some(e => e.field?.includes('cartId') || /cart not found|cart does not exist/i.test(e.message));
}

function cartAnswer(payload?: { cart?: ShopifyCart | null; userErrors?: UserError[]; warnings?: CartWarning[] }): CartAnswer {
  const userErrors = payload?.userErrors || [];
  if (isCartNotFoundError(userErrors)) return { cartNotFound: true };
  if (userErrors.length > 0 || !payload?.cart) return {};
  return { cart: payload.cart, warnings: payload.warnings || [] };
}

async function createShopifyCart(item: CartItem): Promise<{ cart: ShopifyCart; checkoutUrl: string; warnings: CartWarning[] } | null> {
  const data = await cartRequest(CART_CREATE_MUTATION, {
    input: {
      lines: [{ quantity: item.quantity, merchandiseId: item.variantId }],
      attributes: attributionAttributes(),
    },
  });

  if (data?.data?.cartCreate?.userErrors?.length > 0) {
    console.error('Cart creation failed:', data.data.cartCreate.userErrors);
    return null;
  }

  const cart: ShopifyCart | undefined = data?.data?.cartCreate?.cart;
  if (!cart?.checkoutUrl) return null;

  return { cart, checkoutUrl: formatCheckoutUrl(cart.checkoutUrl), warnings: data.data.cartCreate.warnings || [] };
}

async function addLineToShopifyCart(cartId: string, item: CartItem): Promise<CartAnswer> {
  const data = await cartRequest(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [{ quantity: item.quantity, merchandiseId: item.variantId }],
  });
  return cartAnswer(data?.data?.cartLinesAdd);
}

async function updateShopifyCartLine(cartId: string, lineId: string, quantity: number): Promise<CartAnswer> {
  const data = await cartRequest(CART_LINES_UPDATE_MUTATION, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });
  return cartAnswer(data?.data?.cartLinesUpdate);
}

async function removeLineFromShopifyCart(cartId: string, lineId: string): Promise<CartAnswer> {
  const data = await cartRequest(CART_LINES_REMOVE_MUTATION, {
    cartId,
    lineIds: [lineId],
  });
  return cartAnswer(data?.data?.cartLinesRemove);
}

// ─────────────────────────────────────────────────────────────
// Reconciling with Shopify
//
// Shopify caps a quantity it can't supply and keeps a sold-out variant as a
// line of quantity 0, both without a userError. Measured 2026-10-14: asking for
// 6 of a pillow with 4 left returned quantity 4, and the drawer still said 6
// while checkout charged for 4. So every line shown takes its quantity, unit
// price and id from the cart Shopify returns; local items only supply the
// product data used to display them.
//
// Why a line came back lower is only said when Shopify says it. A write that
// hits the stock limit carries a MERCHANDISE_NOT_ENOUGH_STOCK warning naming the
// line; a quantity that is merely lower than this tab remembers (lowered in
// another tab, or a cart saved before quantities were read back) carries none,
// and "Only 1 available" would then be a stock claim with nothing behind it.

/**
 * The line a shopper just changed, and the quantity they set it to. Null for an
 * add: Shopify adds to whatever the line holds, which another tab may have
 * changed, so there is no figure to hold the answer against, only its warnings.
 */
type LineChange = { item: Omit<CartItem, 'lineId'>; quantity: number | null };

const lineName = (item: Pick<CartItem, 'product' | 'variantTitle'>) =>
  item.variantTitle && item.variantTitle !== 'Default Title'
    ? `${item.product.node.title} (${item.variantTitle})`
    : item.product.node.title;

/** A line this tab holds no product data for (added in another tab): enough to show it. */
function itemFromLine(line: ShopifyCartLine): CartItem {
  const variant = line.merchandise;
  const price = line.cost.amountPerQuantity;
  return {
    lineId: line.id,
    variantId: variant.id,
    variantTitle: variant.title,
    price,
    quantity: line.quantity,
    selectedOptions: variant.selectedOptions,
    product: {
      node: {
        id: variant.product.id,
        title: variant.product.title,
        description: '',
        handle: variant.product.handle,
        productType: variant.product.productType,
        priceRange: { minVariantPrice: price },
        images: { edges: variant.product.featuredImage ? [{ node: variant.product.featuredImage }] : [] },
        variants: {
          edges: [{
            node: {
              id: variant.id,
              title: variant.title,
              price,
              availableForSale: variant.availableForSale,
              selectedOptions: variant.selectedOptions,
            },
          }],
        },
        options: [],
      },
    },
  };
}

function reconcile(local: CartItem[], cart: ShopifyCart, change?: LineChange, warnings: CartWarning[] = []) {
  // Ids of the lines Shopify cut down to the stock it has.
  const short = new Set(warnings.filter(w => w.code === 'MERCHANDISE_NOT_ENOUGH_STOCK').map(w => w.target));
  const lines = new Map(cart.lines.edges.map(({ node }) => [node.merchandise.id, node]));
  const sellable = (line: ShopifyCartLine) => line.quantity > 0 && line.merchandise.availableForSale;
  const isNew = !!change && !local.some(i => i.variantId === change.item.variantId);
  const known: CartItem[] = isNew ? [...local, { ...change.item, lineId: null, quantity: 0 }] : local;

  const items: CartItem[] = [];
  const notices: Record<string, LineNotice> = {};
  const removed: Array<{ variantId: string; message: string }> = [];
  // Lines Shopify still counts in its subtotal although the variant has sold out.
  const unsellable: string[] = [];
  // Every line Shopify holds that is not shown here, a sold-out add it keeps at
  // quantity 0 included: left in the cart, checkout opens on "Out of stock".
  const leftover: string[] = [];
  let shortfall: string | undefined;

  for (const item of known) {
    const line = lines.get(item.variantId);
    lines.delete(item.variantId);
    const changed = change?.item.variantId === item.variantId;

    if (!line || !sellable(line)) {
      if (line) leftover.push(line.id);
      if (line && line.quantity > 0) unsellable.push(line.id);
      // The shopper took this line out themselves: nothing to explain.
      if (changed && change.quantity === 0) continue;
      if (item.quantity > 0) {
        // "Sold out" only when Shopify marks the variant so. No line at all means
        // it was removed in another tab or delisted by the supplier, and Shopify
        // doesn't say which.
        const message = line && !line.merchandise.availableForSale
          ? `${lineName(item)} sold out and was removed from your cart.`
          : `${lineName(item)} is no longer in your cart.`;
        removed.push({ variantId: item.variantId, message });
        if (changed) shortfall = message;
      } else if (line && !line.merchandise.availableForSale) {
        shortfall = `${lineName(item)} is sold out and wasn't added to your cart.`;
      }
      continue;
    }

    const price = line.cost.amountPerQuantity;
    const expected = changed ? change.quantity : item.quantity;
    const capped = short.has(line.id)
      ? `Only ${line.quantity} available, so your cart has ${line.quantity}.`
      : expected !== null && line.quantity < expected ? `Your cart now has ${line.quantity} of this item.` : '';
    const repriced = parseFloat(price.amount) !== parseFloat(item.price.amount)
      ? `The price is now ${formatPrice(price.amount, price.currencyCode)}.`
      : '';
    if (capped || repriced) notices[item.variantId] = { message: [capped, repriced].filter(Boolean).join(' '), at: Date.now() };
    if (changed && capped) shortfall = capped;

    const same = item.lineId === line.id && item.quantity === line.quantity
      && item.price.amount === price.amount && item.price.currencyCode === price.currencyCode;
    // Untouched lines keep their identity so effects keyed on `items` don't re-run.
    items.push(same ? item : { ...item, lineId: line.id, quantity: line.quantity, price });
  }

  // Whatever else Shopify holds was added in another tab; checkout will charge for it.
  for (const line of lines.values()) {
    if (sellable(line)) items.push(itemFromLine(line));
    else {
      leftover.push(line.id);
      if (line.quantity > 0) unsellable.push(line.id);
    }
  }

  const untouched = items.length === local.length && items.every((item, i) => item === local[i]);
  return { items: untouched ? local : items, notices, removed, unsellable, leftover, shortfall };
}

// A line notice answers something the shopper just did. The stock or price it
// describes can move again, so it is not shown on a later visit.
const NOTICE_TTL_MS = 30 * 60 * 1000;

const freshNotices = (notices: Record<string, LineNotice> = {}) =>
  Object.fromEntries(Object.entries(notices).filter(([, notice]) => Date.now() - notice?.at < NOTICE_TTL_MS));

/**
 * Make the local cart match a cart Shopify returned. The only place lines are
 * written. Returns what to tell the shopper when the line they changed came
 * back with less than they asked for. `warnings` are the ones Shopify sent
 * with this cart; a read has none.
 */
async function applyCart(cart: ShopifyCart, change?: LineChange, warnings?: CartWarning[]): Promise<string | undefined> {
  const state = useCartStore.getState();
  const { items, notices, removed, unsellable, leftover, shortfall } = reconcile(state.items, cart, change, warnings);
  // One reason per line, dropped again if the line is back in the cart.
  const removedNotices = [
    ...state.removedNotices.filter(old => !removed.some(r => r.variantId === old.variantId)),
    ...removed,
  ].filter(r => !items.some(i => i.variantId === r.variantId));

  if (items.length === 0) {
    // Nothing left to buy. Keep the reasons so an emptied cart explains itself.
    useCartStore.setState({ items: [], cartId: null, checkoutUrl: null, subtotal: null, notices: {}, removedNotices });
    return shortfall;
  }

  // A line's earlier notice goes once the shopper changes that line or it leaves the cart.
  const kept = Object.entries(freshNotices(state.notices)).filter(
    ([variantId]) => variantId !== change?.item.variantId && items.some(i => i.variantId === variantId),
  );
  useCartStore.setState({
    items,
    // With an unsellable line still counted, Shopify's subtotal is for more than
    // the lines shown; the sum of those lines is what checkout will charge.
    subtotal: unsellable.length ? null : parseFloat(cart.cost.subtotalAmount.amount),
    notices: { ...Object.fromEntries(kept), ...notices },
    removedNotices,
  });

  if (leftover.length) {
    try {
      await cartRequest(CART_LINES_REMOVE_MUTATION, { cartId: cart.id, lineIds: leftover });
    } catch {
      /* checkout drops the line anyway; the total shown already leaves it out */
    }
  }
  return shortfall;
}

/** Read the cart from Shopify and make the local lines match it. */
async function pullCart(cartId: string) {
  const data = await cartRequest(CART_QUERY, { id: cartId });
  if (!data) return;
  // Emptied or replaced while this read was on its way (the thank-you page
  // clears the cart outside the queue): putting its lines back would leave
  // lines with no cartId or checkoutUrl, which cannot be changed or checked out.
  if (useCartStore.getState().cartId !== cartId) return;
  const cart: ShopifyCart | null = data?.data?.cart;
  // No cart: it was checked out or has expired.
  if (!cart) useCartStore.getState().clearCart();
  else await applyCart(cart);
}

// Each answer carries the whole cart and the local lines are rebuilt from it,
// so cart calls run one at a time: two answers landing out of order would
// leave the older cart on screen.
let lane: Promise<unknown> = Promise.resolve();
let mutating = 0;

function inTurn<T>(job: () => Promise<T>): Promise<T> {
  const run = lane.then(job);
  lane = run.then(() => undefined, () => undefined);
  return run;
}

function mutate<T>(job: () => Promise<T>): Promise<T> {
  mutating++;
  useCartStore.setState({ isLoading: true });
  return inTurn(job).finally(() => {
    if (--mutating === 0) useCartStore.setState({ isLoading: false });
  });
}

const TOAST = { position: 'top-center' } as const;
const UPDATE_FAILED = "Couldn't update the quantity. Please try again.";
const REMOVE_FAILED = "Couldn't remove the item. Please try again.";

/**
 * Take the cart another tab saved. A tab opened before any cart existed still
 * has cartId null in memory; its first add used to start a second Shopify cart
 * and overwrite the saved one, so the first tab's item never reached checkout.
 * (rehydrate reads localStorage through the store's own merge; it writes nothing.)
 */
async function adoptSavedCart() {
  try {
    await useCartStore.persist.rehydrate();
  } catch {
    /* storage unavailable: carry on with this tab's cart */
  }
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      subtotal: null,
      notices: {},
      removedNotices: [],
      isLoading: false,
      isSyncing: false,

      addItem: (item) => mutate<CartChange>(async () => {
        const editorial = getEditorialSource();
        const failed = (): CartChange => {
          toast.error("Couldn't add to cart. Please try again.", TOAST);
          return { ok: false, addedQuantity: 0 };
        };
        try {
          if (!get().cartId) await adoptSavedCart();
          let cartId = get().cartId;
          let before = get().items.find(i => i.variantId === item.variantId);
          let cart: ShopifyCart | undefined;
          let warnings: CartWarning[] | undefined;

          if (cartId) {
            // Always an add, also for a variant already in the cart: Shopify puts
            // it on the line it holds. Setting that line to "what this tab has + n"
            // failed when another tab had removed the line, and lowered the
            // quantity when another tab had raised it.
            const answer = await addLineToShopifyCart(cartId, { ...item, lineId: null });
            if (answer.cartNotFound) {
              // Checked out or expired: start a new cart with this item rather than lose the add.
              get().clearCart();
              cartId = null;
              before = undefined;
            } else if (!answer.cart) {
              await pullCart(cartId);
              return failed();
            } else {
              ({ cart, warnings } = answer);
            }
          }

          if (!cart) {
            const created = await createShopifyCart({ ...item, lineId: null });
            if (!created) return failed();
            set({ cartId: created.cart.id, checkoutUrl: created.checkoutUrl });
            ({ cart, warnings } = created);
          }

          const had = before?.quantity ?? 0;
          const notice = await applyCart(cart, { item, quantity: null }, warnings);
          const line = get().items.find(i => i.variantId === item.variantId);
          // Taken in full unless Shopify said otherwise; then the rise this tab can see.
          const addedQuantity = !line ? 0 : notice ? Math.min(item.quantity, Math.max(0, line.quantity - had)) : item.quantity;

          if (line && addedQuantity > 0) {
            void saveEmailOutcome('cart_add', item.product.node.handle, addedQuantity).catch(() => undefined);
            if (editorial && getEditorialSource()) {
              saveEditorialOutcome('cart_add', editorial, item.product.node.handle, addedQuantity);
              window.__lovableEvents?.track?.('project.editorial_cart_add', { source_kind: editorial.kind, source_slug: editorial.slug, product_handle: item.product.node.handle, quantity: addedQuantity });
            }
            // Track analytics
            window.__lovableEvents?.track?.('project.product_added_to_cart', {
              product_id: item.product.node.id,
              category: item.product.node.productType || 'Uncategorized',
              value: parseFloat(line.price.amount) * addedQuantity,
              currency: line.price.currencyCode,
              quantity: addedQuantity,
            });
            trackAddToCart({
              id: item.variantId,
              name: item.product.node.title,
              price: parseFloat(line.price.amount),
              quantity: addedQuantity,
              currency: line.price.currencyCode,
            });
          }
          if (!line && !notice) return failed();
          if (notice && line) toast.warning(notice, TOAST);
          else if (notice) toast.error(notice, TOAST);
          return { ok: !!line, addedQuantity, notice };
        } catch (error) {
          console.error('Failed to add item:', error);
          // No answer is not "not added": read the cart back, so a retry of an
          // add that did land is made with that line on screen.
          void get().syncCart();
          return failed();
        }
      }),

      updateQuantity: (variantId, quantity) => {
        if (quantity <= 0) return get().removeItem(variantId);

        return mutate(async () => {
          const { items, cartId, clearCart } = get();
          const item = items.find(i => i.variantId === variantId);
          if (!item?.lineId || !cartId) return false;

          try {
            const answer = await updateShopifyCartLine(cartId, item.lineId, quantity);
            if (answer.cartNotFound) {
              clearCart();
              return false;
            }
            if (answer.cart) {
              const notice = await applyCart(answer.cart, { item, quantity }, answer.warnings);
              if (notice) toast.warning(notice, TOAST);
              return true;
            }
            // Refused: show what Shopify holds. A line that is gone explains
            // itself in the cart's notice; one still there needs saying.
            await pullCart(cartId);
            if (get().items.some(i => i.variantId === variantId)) toast.error(UPDATE_FAILED, TOAST);
            return false;
          } catch (error) {
            console.error('Failed to update quantity:', error);
            toast.error(UPDATE_FAILED, TOAST);
            // The change may have landed without its answer: read the cart back.
            void get().syncCart();
            return false;
          }
        });
      },

      removeItem: (variantId) => mutate(async () => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item?.lineId || !cartId) return false;

        try {
          const answer = await removeLineFromShopifyCart(cartId, item.lineId);
          if (answer.cartNotFound) clearCart();
          else if (answer.cart) await applyCart(answer.cart, { item, quantity: 0 }, answer.warnings);
          else await pullCart(cartId);
          // Refused and still in the cart: say so.
          const gone = !get().items.some(i => i.variantId === variantId);
          if (!gone) toast.error(REMOVE_FAILED, TOAST);
          return gone;
        } catch (error) {
          console.error('Failed to remove item:', error);
          toast.error(REMOVE_FAILED, TOAST);
          void get().syncCart();
          return false;
        }
      }),

      clearCart: () => set({ items: [], cartId: null, checkoutUrl: null, subtotal: null, notices: {}, removedNotices: [] }),
      dismissRemovedNotices: () => set({ removedNotices: [] }),
      getCheckoutUrl: () => get().checkoutUrl,

      setGiftNote: async (message) => {
        const { cartId } = get();
        const note = message.trim();
        if (!cartId || !note) return;
        try {
          await storefrontApiRequest(CART_ATTRIBUTES_UPDATE_MUTATION, {
            cartId,
            attributes: [
              ...attributionAttributes(),
              { key: 'Gift message', value: note.slice(0, 200) },
            ],
          });
        } catch (error) {
          console.error('Failed to attach gift note:', error);
        }
      },

      syncCart: async () => {
        if (!get().cartId || get().isSyncing) return;

        set({ isSyncing: true });
        try {
          await inTurn(async () => {
            // Read again: a change queued ahead of this may have replaced or emptied the cart.
            const { cartId } = get();
            if (cartId) await pullCart(cartId);
          });
        } catch (error) {
          console.error('Failed to sync cart:', error);
        } finally {
          set({ isSyncing: false });
        }
      },

      getTotalItems: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      getTotalPrice: () => {
        const { subtotal, items } = get();
        return subtotal ?? items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
      },
    }),
    {
      name: 'loftloom-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
        cartId: state.cartId,
        checkoutUrl: state.checkoutUrl,
        subtotal: state.subtotal,
        notices: state.notices,
        removedNotices: state.removedNotices,
      }),
      // As saved, except line notices from an earlier visit.
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<CartStore>;
        return { ...current, ...saved, notices: freshNotices(saved.notices) };
      },
    }
  )
);

// Every open tab follows the cart the last write saved (badge, drawer, /cart),
// in turn with its own cart calls.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'loftloom-cart') void inTurn(adoptSavedCart);
  });
}
